import { useEffect, useState, useCallback } from "react";
import { 
  getInventory, 
  uploadInventory, 
  downloadInventory,
  updateInventoryItem,
  getBrands,
  syncShopify,
  api
} from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { ScrollArea } from "../components/ui/scroll-area";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { 
  Upload, 
  Download, 
  Package, 
  Search,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  ShoppingBag,
  Tag,
  Box,
  Settings2,
  CloudDownload
} from "lucide-react";
import { toast } from "sonner";

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const YEARS = ["24", "25", "26", "27", "28"];

export default function InventoryLegacy() {
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [brands, setBrands] = useState(["OBLIK"]);
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [stockFilter, setStockFilter] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null);
  const [importMonth, setImportMonth] = useState("02");
  const [importYear, setImportYear] = useState("26");
  const [syncingSheets, setSyncingSheets] = useState(false);

  useEffect(() => {
    loadBrands();
    loadInventory();
  }, []);

  const filterInventory = useCallback(() => {
    let filtered = [...inventory];
    
    if (selectedBrand && selectedBrand !== "all") {
      filtered = filtered.filter(item => item.brand === selectedBrand);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.model_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (stockFilter === "low") {
      filtered = filtered.filter(item => item.quantity > 0 && item.quantity <= 10);
    } else if (stockFilter === "out") {
      filtered = filtered.filter(item => item.quantity === 0);
    }
    
    setFilteredInventory(filtered);
  }, [inventory, selectedBrand, searchTerm, stockFilter]);

  useEffect(() => {
    filterInventory();
  }, [filterInventory]);

  const loadBrands = async () => {
    try {
      const response = await getBrands();
      if (response.data && response.data.length > 0) {
        setBrands(response.data);
      }
    } catch (error) {
      console.error("Failed to load brands");
    }
  };

  const handleSyncFromSheets = async () => {
    toast.error("Google Sheets sync has been permanently disabled.");
  };

  const loadInventory = async () => {
    try {
      const response = await getInventory();
      setInventory(response.data);
    } catch (error) {
      toast.error("Failed to load inventory");
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const response = await uploadInventory(file);
      toast.success(`Uploaded ${response.data.count} items for brand: ${response.data.brand}`);
      await loadBrands();
      await loadInventory();
    } catch (error) {
      toast.error("Failed to upload inventory");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await downloadInventory();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'inventory.csv';
      a.click();
      toast.success("Inventory downloaded");
    } catch (error) {
      toast.error("Failed to download inventory");
    }
  };

  const handleShopifySync = async () => {
    setSyncing(true);
    try {
      const response = await syncShopify();
      if (response.data.status === 'success') {
        toast.success(`Synced ${response.data.orders_processed} Shopify orders`);
        await loadInventory();
      } else {
        toast.error(response.data.message || "Sync failed");
      }
    } catch (error) {
      toast.error("Shopify sync not configured");
    } finally {
      setSyncing(false);
    }
  };

  const handleQuantityUpdate = async (srNo, newQuantity) => {
    try {
      await updateInventoryItem(srNo, { quantity: parseInt(newQuantity) });
      toast.success("Quantity updated");
      await loadInventory();
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const handlePrintBoxTag = async (item) => {
    const month = item.import_month || `${importMonth}/${importYear}`;
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.sr_no}/box-tag?import_month=${encodeURIComponent(month)}`;
    window.open(url, '_blank');
  };

  const handlePrintProductTag = async (item) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.sr_no}/product-tag`;
    window.open(url, '_blank');
  };

  const handleUpdateImportMonth = async () => {
    if (!selectedItem) return;
    try {
      await api.put(`/inventory/${selectedItem.sr_no}/import-month`, {
        import_month: `${importMonth}/${importYear}`
      });
      toast.success("Import month updated");
      await loadInventory();
      setSelectedItem(null);
    } catch (error) {
      toast.error("Failed to update import month");
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) {
      return <Badge className="bg-red-100 text-red-700">Out</Badge>;
    } else if (quantity <= 10) {
      return <Badge className="bg-amber-100 text-amber-700">Low</Badge>;
    }
    return <Badge className="bg-emerald-100 text-emerald-700">OK</Badge>;
  };

  const getStats = (brandFilter = "all") => {
    const items = brandFilter === "all" ? inventory : inventory.filter(i => i.brand === brandFilter);
    return {
      total: items.length,
      totalStock: items.reduce((sum, item) => sum + (item.quantity || 0), 0),
      lowStock: items.filter(item => item.quantity > 0 && item.quantity <= 10).length,
      outOfStock: items.filter(item => item.quantity === 0).length
    };
  };

  const stats = getStats(selectedBrand);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="inventory-page">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Package className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.total}</p>
                <p className="text-xs text-slate-500">Total SKUs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-emerald-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.totalStock}</p>
                <p className="text-xs text-slate-500">Total Units</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-amber-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.lowStock}</p>
                <p className="text-xs text-slate-500">Low Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold font-mono">{stats.outOfStock}</p>
                <p className="text-xs text-slate-500">Out of Stock</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Actions bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant={stockFilter === "all" ? "default" : "outline"}
                size="sm"
                onClick={() => setStockFilter("all")}
              >
                All ({stats.total})
              </Button>
              <Button
                variant={stockFilter === "low" ? "default" : "outline"}
                size="sm"
                onClick={() => setStockFilter("low")}
              >
                Low ({stats.lowStock})
              </Button>
              <Button
                variant={stockFilter === "out" ? "default" : "outline"}
                size="sm"
                onClick={() => setStockFilter("out")}
              >
                Out ({stats.outOfStock})
              </Button>
            </div>
            
            <div className="flex items-center gap-2 w-full md:w-auto flex-wrap">
              <div className="relative flex-1 md:w-48">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search..."
                  className="pl-9"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <Button variant="outline" size="icon" onClick={loadInventory} title="Refresh">
                <RefreshCw className="w-4 h-4" />
              </Button>
              
              <Button variant="outline" size="icon" onClick={handleShopifySync} disabled={syncing} title="Shopify Sync">
                {syncing ? <div className="spinner w-4 h-4" /> : <ShoppingBag className="w-4 h-4" />}
              </Button>
              
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-1" />Export
              </Button>
              
              <div className="relative">
                <Input type="file" accept=".csv,.xlsx,.xls" className="absolute inset-0 opacity-0 cursor-pointer" onChange={handleUpload} disabled={uploading} />
                <Button disabled={uploading} size="sm">
                  {uploading ? <div className="spinner w-4 h-4 mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                  Import
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Brand Tabs */}
      <Tabs defaultValue="all" value={selectedBrand} onValueChange={setSelectedBrand}>
        <TabsList className="mb-4">
          <TabsTrigger value="all">All Brands</TabsTrigger>
          {brands.map(brand => (
            <TabsTrigger key={brand} value={brand}>{brand}</TabsTrigger>
          ))}
        </TabsList>

        {/* Inventory table */}
        <Card>
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-sm font-medium">
              Inventory Items ({filteredInventory.length})
              {selectedBrand !== "all" && <Badge variant="secondary" className="ml-2">{selectedBrand}</Badge>}
            </CardTitle>
          </CardHeader>
          <ScrollArea className="h-[450px]">
            {filteredInventory.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <Package className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg">No inventory items found</p>
              </div>
            ) : (
              <Table className="data-table">
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase w-12">#</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Product</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-20">Qty</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right w-24">MRP</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-16">Status</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-center w-32">Tags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInventory.map((item) => (
                    <TableRow 
                      key={item.sr_no}
                      className={`${item.quantity === 0 ? 'bg-red-50' : ''} ${item.quantity > 0 && item.quantity <= 10 ? 'bg-amber-50' : ''}`}
                    >
                      <TableCell className="font-mono text-sm">{item.sr_no}</TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{item.model_name}</p>
                          <p className="text-xs text-slate-500">{item.brand || 'OBLIK'}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          className="w-16 h-7 text-center font-mono text-sm mx-auto"
                          value={item.quantity}
                          onChange={(e) => handleQuantityUpdate(item.sr_no, e.target.value)}
                          min="0"
                        />
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        Rs. {item.mrp?.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-center">
                        {getStockStatus(item.quantity)}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handlePrintProductTag(item)}
                            title="Print Product Tag (38x15mm)"
                          >
                            <Tag className="w-3 h-3 mr-1" />
                            38x15
                          </Button>
                          
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => {
                                  setSelectedItem(item);
                                  const parts = (item.import_month || "02/26").split("/");
                                  setImportMonth(parts[0] || "02");
                                  setImportYear(parts[1] || "26");
                                }}
                                title="Print Box Tag (50x25mm)"
                              >
                                <Box className="w-3 h-3 mr-1" />
                                50x25
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-md">
                              <DialogHeader>
                                <DialogTitle>Box Tag Settings</DialogTitle>
                                <DialogDescription>Set the month of import for this product's box tag</DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4 py-4">
                                <div>
                                  <p className="text-sm font-medium mb-2">{item.model_name}</p>
                                  <p className="text-sm text-slate-500">MRP: Rs. {item.mrp?.toLocaleString()}</p>
                                </div>
                                
                                <div className="space-y-2">
                                  <Label>Month of Import</Label>
                                  <div className="flex gap-2">
                                    <Select value={importMonth} onValueChange={setImportMonth}>
                                      <SelectTrigger className="w-32">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {MONTHS.map(m => (
                                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    <Select value={importYear} onValueChange={setImportYear}>
                                      <SelectTrigger className="w-20">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {YEARS.map(y => (
                                          <SelectItem key={y} value={y}>20{y}</SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                <div className="flex gap-2 pt-2">
                                  <Button 
                                    variant="outline" 
                                    className="flex-1"
                                    onClick={handleUpdateImportMonth}
                                  >
                                    <Settings2 className="w-4 h-4 mr-2" />
                                    Save Default
                                  </Button>
                                  <Button 
                                    className="flex-1"
                                    onClick={() => {
                                      const month = `${importMonth}/${importYear}`;
                                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/inventory/${item.sr_no}/box-tag?import_month=${encodeURIComponent(month)}`;
                                      window.open(url, '_blank');
                                    }}
                                  >
                                    <Box className="w-4 h-4 mr-2" />
                                    Print Tag
                                  </Button>
                                </div>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </ScrollArea>
        </Card>
      </Tabs>
    </div>
  );
}
